import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Inbox, Mail, MailOpen, Paperclip, Pencil, RefreshCw, Reply, Search, Send, ShieldAlert } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking, isAppAdmin } from "@/lib/permissions";
import { MailMessageBody } from "@/components/mail/MailMessageBody";
import { PhoneBookingHoldModal } from "@/pages/bookings/PhoneBookingHoldModal";
import {
  analyzeInquiryMail,
  buildMailReplySeed,
  primaryGuestFieldsFromSettings,
  type MailInquiryAnalysis,
  type MailReplySeed,
} from "@/lib/mailReservationReply";
import { buildBookingsOpenUrl } from "@/lib/bookingNavigation";
import { mailboxReadStateHint, mailboxReadStateMode } from "@/lib/platform";
import type { DepositSettings } from "@/pages/stammdaten/settings/tenantSettingsTypes";
import type { CompanionFieldsConfig } from "@/lib/companionFields";
import type { PitchCategory } from "@/types/camping";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import { GuestStayHistory } from "@/components/guests/GuestStayHistory";
import { guestTypeLabel } from "@/lib/guestType";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Guest, GuestStay } from "@/types/camping";

type MailFolder = "inbox" | "sent";

const PAGE_SIZE = 50;

interface MailGuestMatch {
  id: string;
  first_name: string;
  last_name: string;
  is_place_banned: boolean;
}

interface InboxMessageSummary {
  id: string;
  subject: string;
  from_name: string | null;
  from_address: string;
  received_at: string;
  is_read: boolean;
  body_preview: string;
  has_attachments: boolean;
  guest_match: MailGuestMatch | null;
}

interface SentMessageSummary {
  id: string;
  subject: string;
  to_name: string | null;
  to_address: string;
  sent_at: string;
  is_read: boolean;
  body_preview: string;
  has_attachments: boolean;
  guest_match: MailGuestMatch | null;
}

type MailMessageSummary = InboxMessageSummary | SentMessageSummary;

interface InboxMessageListResponse {
  mailbox: string;
  items: InboxMessageSummary[];
  next_skip: number | null;
}

interface SentMessageListResponse {
  mailbox: string;
  items: SentMessageSummary[];
  next_skip: number | null;
}

interface InboxMessageDetail extends InboxMessageSummary {
  body_text: string;
  body_html: string;
  to_addresses: string[];
  guest: Guest | null;
  guest_stays: GuestStay[];
}

interface SentMessageDetail extends SentMessageSummary {
  body_text: string;
  body_html: string;
  to_addresses: string[];
  from_name: string | null;
  from_address: string;
  guest: Guest | null;
  guest_stays: GuestStay[];
}

type MailMessageDetail = InboxMessageDetail | SentMessageDetail;

function formatMailTimestamp(iso: string): string {
  if (!iso) return "—";
  return formatAuditTimestamp(iso);
}

function isInboxMessage(message: MailMessageSummary): message is InboxMessageSummary {
  return "received_at" in message;
}

function messageTimestamp(message: MailMessageSummary): string {
  return isInboxMessage(message) ? message.received_at : message.sent_at;
}

function messageContactLabel(message: MailMessageSummary): string {
  if (isInboxMessage(message)) {
    return message.from_name || message.from_address;
  }
  return message.to_name || message.to_address;
}

function guestMatchLabel(guest: MailGuestMatch): string {
  return `${guest.last_name}, ${guest.first_name}`;
}

const CLOSED_STAY_STATUSES = new Set(["rejected", "cancelled", "checked_out"]);

function upcomingStays(stays: GuestStay[], today: string): GuestStay[] {
  return stays
    .filter((stay) => stay.departure_date >= today && !CLOSED_STAY_STATUSES.has(stay.status))
    .sort((a, b) => a.arrival_date.localeCompare(b.arrival_date));
}

function stayOverlapsPeriod(stay: GuestStay, arrival: string, departure: string): boolean {
  return stay.arrival_date < departure && stay.departure_date > arrival;
}

function GuestMatchBadge({ guest }: { guest: MailGuestMatch }) {
  return (
    <span
      className={clsx(
        "inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[0.65rem] font-semibold",
        guest.is_place_banned
          ? "bg-status-red/15 text-status-red"
          : "bg-accent/15 text-accent",
      )}
    >
      <span className="shrink-0">{guest.is_place_banned ? "Platzverbot" : "Gast"}</span>
      <span className="truncate font-medium">{guestMatchLabel(guest)}</span>
    </span>
  );
}

function ReadStatusBadge({ isRead }: { isRead: boolean }) {
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-medium",
        isRead
          ? "bg-fill-tertiary text-label-secondary"
          : "bg-status-orange/15 text-status-orange",
      )}
    >
      {isRead ? "Gelesen" : "Ungelesen"}
    </span>
  );
}

function GuestMailContext({
  guest,
  stays,
}: {
  guest: Guest;
  stays: GuestStay[];
}) {
  const addressParts = [guest.street, guest.postal_code, guest.city].filter(Boolean);

  return (
    <aside className="space-y-4 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-label">
          {guest.last_name}, {guest.first_name}
        </h3>
        {guest.is_place_banned ? <PlaceBanBadge prominent /> : null}
      </div>

      <dl className="grid gap-2 text-sm">
        <div>
          <dt className="text-xs text-label-tertiary">Gasttyp</dt>
          <dd>{guestTypeLabel(guest.guest_type)}</dd>
        </div>
        {guest.email ? (
          <div>
            <dt className="text-xs text-label-tertiary">E-Mail</dt>
            <dd>{guest.email}</dd>
          </div>
        ) : null}
        {guest.phone || guest.phone_mobile ? (
          <div>
            <dt className="text-xs text-label-tertiary">Telefon</dt>
            <dd>{guest.phone_mobile || guest.phone}</dd>
          </div>
        ) : null}
        {addressParts.length > 0 ? (
          <div>
            <dt className="text-xs text-label-tertiary">Adresse</dt>
            <dd>{addressParts.join(", ")}</dd>
          </div>
        ) : null}
        {guest.date_of_birth ? (
          <div>
            <dt className="text-xs text-label-tertiary">Geburtsdatum</dt>
            <dd>{formatIsoDateShort(guest.date_of_birth)}</dd>
          </div>
        ) : null}
        {guest.id_document_number ? (
          <div>
            <dt className="text-xs text-label-tertiary">Ausweis</dt>
            <dd>{guest.id_document_number}</dd>
          </div>
        ) : null}
      </dl>

      {guest.is_place_banned && guest.place_ban_reason ? (
        <div className="rounded-lg border border-status-red/40 bg-status-red/10 px-3 py-2 text-sm text-status-red">
          <span className="font-bold">Platzverbot: </span>
          {guest.place_ban_reason}
        </div>
      ) : null}

      {guest.notes ? (
        <p className="text-sm text-label-secondary whitespace-pre-wrap">
          <span className="font-medium text-label">Notizen: </span>
          {guest.notes}
        </p>
      ) : null}

      {stays.length > 0 ? (
        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Letzte Aufenthalte
          </h4>
          <ul className="space-y-2">
            {stays.slice(0, 5).map((stay) => (
              <li
                key={stay.id}
                className="rounded-lg border border-separator bg-card px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{stay.booking_number}</span>
                  <StatusBadge status={stay.status} />
                </div>
                <p className="mt-0.5 text-xs text-label-secondary">
                  {formatIsoDateShort(stay.arrival_date)} → {formatIsoDateShort(stay.departure_date)}
                  {stay.pitch_code ? ` · Platz ${stay.pitch_code}` : ""}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <GuestStayHistory guestId={guest.id} active />
      )}

      <Link
        to={`/guests/${guest.id}`}
        className="inline-flex text-sm font-medium text-accent hover:underline"
      >
        Gast in Stammkartei öffnen
      </Link>
    </aside>
  );
}

export function MailPage() {
  const { setPageMeta } = usePageHeader();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const groups = (user?.groups as string[] | undefined) ?? [];
  const canReply = canWriteGuestPitchBooking(groups);
  const canErase = isAppAdmin(groups);
  const readStateMode = mailboxReadStateMode();
  const readStateLive = readStateMode === "live";
  const readStateHint = mailboxReadStateHint(readStateMode);
  const [folder, setFolder] = useState<MailFolder>("inbox");
  const [mailbox, setMailbox] = useState("");
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MailMessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextSkip, setNextSkip] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [guestDetailsOpen, setGuestDetailsOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySeed, setReplySeed] = useState<MailReplySeed | null>(null);
  const [replyPreparing, setReplyPreparing] = useState(false);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  const keepUnreadIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handle = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const unreadCount = useMemo(
    () => messages.filter((message) => !message.is_read).length,
    [messages],
  );

  useEffect(() => {
    if (categories.length > 0) return;
    void api.get<PitchCategory[]>("/api/v1/pitch-categories").then(setCategories).catch(() => undefined);
  }, [categories.length]);

  const fetchMessagePage = useCallback(
    async (skip: number) => {
      const params = new URLSearchParams({
        top: String(PAGE_SIZE),
        skip: String(skip),
      });
      if (searchQuery.length >= 2) params.set("q", searchQuery);
      const endpoint =
        folder === "inbox"
          ? `/api/v1/mail/inbox?${params.toString()}`
          : `/api/v1/mail/sent?${params.toString()}`;
      return api.get<InboxMessageListResponse | SentMessageListResponse>(endpoint);
    },
    [folder, searchQuery],
  );

  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMessagePage(0);
      setMailbox(result.mailbox);
      setMessages(result.items);
      setNextSkip(result.next_skip);
      const currentSelectedId = selectedIdRef.current;
      if (currentSelectedId && !result.items.some((m) => m.id === currentSelectedId)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : folder === "inbox"
            ? "Posteingang konnte nicht geladen werden."
            : "Postausgang konnte nicht geladen werden.",
      );
      setMessages([]);
      setNextSkip(null);
    } finally {
      setLoading(false);
    }
  }, [fetchMessagePage, folder]);

  const loadMoreMessages = useCallback(async () => {
    if (nextSkip === null || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const result = await fetchMessagePage(nextSkip);
      setMessages((current) => {
        const existingIds = new Set(current.map((message) => message.id));
        const newItems = result.items.filter((message) => !existingIds.has(message.id));
        return newItems.length > 0 ? [...current, ...newItems] : current;
      });
      setNextSkip(result.next_skip);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : folder === "inbox"
            ? "Weitere Nachrichten konnten nicht geladen werden."
            : "Weitere gesendete Nachrichten konnten nicht geladen werden.",
      );
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMessagePage, folder, loading, loadingMore, nextSkip]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!sentinel || !root || nextSkip === null || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMoreMessages();
        }
      },
      { root, rootMargin: "240px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreMessages, loading, loadingMore, messages.length, nextSkip]);

  const setMessageReadState = useCallback((messageId: string, isRead: boolean, silent = false) => {
    if (isRead) keepUnreadIdsRef.current.delete(messageId);
    else keepUnreadIdsRef.current.add(messageId);

    const apply = (value: boolean) => {
      setMessages((current) =>
        current.map((message) =>
          message.id === messageId ? { ...message, is_read: value } : message,
        ),
      );
      setDetail((current) =>
        current && current.id === messageId ? { ...current, is_read: value } : current,
      );
    };

    apply(isRead);
    void api
      .patch(`/api/v1/mail/inbox/${encodeURIComponent(messageId)}/read`, { is_read: isRead })
      .catch(() => {
        if (silent) return;
        if (isRead) keepUnreadIdsRef.current.add(messageId);
        else keepUnreadIdsRef.current.delete(messageId);
        apply(!isRead);
        setError("Lesestatus konnte nicht geändert werden.");
      });
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "E-Mail",
      subtitle: folder === "inbox" ? "Posteingang" : "Postausgang",
    });
    return () => setPageMeta(null);
  }, [folder, setPageMeta]);

  useEffect(() => {
    setSelectedId(null);
    setDetail(null);
  }, [folder]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setError(null);
    const endpoint =
      folder === "inbox"
        ? `/api/v1/mail/inbox/${encodeURIComponent(selectedId)}`
        : `/api/v1/mail/sent/${encodeURIComponent(selectedId)}`;
    void api
      .get<MailMessageDetail>(endpoint)
      .then((result) => {
        const markRead =
          readStateLive &&
          folder === "inbox" &&
          !result.is_read &&
          !keepUnreadIdsRef.current.has(selectedId);
        setDetail(markRead ? { ...result, is_read: true } : result);
        if (markRead) setMessageReadState(selectedId, true, true);
      })
      .catch((e: Error) => {
        setError(e.message);
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  }, [folder, readStateLive, selectedId, setMessageReadState]);

  useEffect(() => {
    setGuestDetailsOpen(false);
  }, [selectedId]);

  const openReservationReply = useCallback(async () => {
    if (!detail || folder !== "inbox" || !("from_address" in detail)) return;
    setReplyPreparing(true);
    setError(null);
    try {
      const [categoryList, settings] = await Promise.all([
        categories.length > 0
          ? Promise.resolve(categories)
          : api.get<PitchCategory[]>("/api/v1/pitch-categories"),
        api.get<{
          deposit: DepositSettings;
          stripe?: { configured?: boolean };
          integrations?: { primary_guest_fields?: Partial<CompanionFieldsConfig> };
        }>("/api/v1/settings"),
      ]);
      if (categories.length === 0) setCategories(categoryList);
      const body = detail.body_text || "";
      setReplySeed(
        buildMailReplySeed({
          email: detail.from_address,
          subject: detail.subject,
          body,
          guest: detail.guest,
          categories: categoryList,
          fields: primaryGuestFieldsFromSettings(settings.integrations?.primary_guest_fields),
          deposit: settings.deposit,
          onlinePayment: Boolean(settings.stripe?.configured),
        }),
      );
      setReplyOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Die Antwort konnte nicht vorbereitet werden.");
    } finally {
      setReplyPreparing(false);
    }
  }, [categories, detail, folder]);

  const selectedSummary = useMemo(
    () => messages.find((m) => m.id === selectedId) ?? null,
    [messages, selectedId],
  );

  const inquiry = useMemo((): MailInquiryAnalysis | null => {
    if (folder !== "inbox" || !detail || !("body_text" in detail)) return null;
    return analyzeInquiryMail({
      subject: detail.subject,
      body: detail.body_text || "",
      categories,
    });
  }, [categories, detail, folder]);

  const openGuestStays = useMemo(() => {
    if (!detail?.guest_stays) return [];
    return upcomingStays(detail.guest_stays, todayLocal());
  }, [detail]);

  const overlappingStay = useMemo(() => {
    if (!inquiry?.arrivalDate) return null;
    return (
      openGuestStays.find((stay) =>
        stayOverlapsPeriod(stay, inquiry.arrivalDate, inquiry.departureDate),
      ) ?? null
    );
  }, [inquiry, openGuestStays]);

  const folderLabel = folder === "inbox" ? "Posteingang" : "Postausgang";
  const emptyListText =
    folder === "inbox"
      ? "Keine Nachrichten im Posteingang."
      : "Keine Nachrichten im Postausgang.";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm text-label-secondary">
            <Mail size={16} aria-hidden />
            Postfach{" "}
            <span className="font-medium text-label">{mailbox || "info@campingplatz-mainkur.de"}</span>
          </p>
          {readStateHint ? <p className="text-xs text-label-tertiary">{readStateHint}</p> : null}
          <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Postfach wählen">
            <button
              type="button"
              role="tab"
              aria-selected={folder === "inbox"}
              className={clsx("eh-segmented-item flex items-center gap-1.5", folder === "inbox" && "is-active")}
              onClick={() => setFolder("inbox")}
            >
              <Inbox size={14} aria-hidden />
              Posteingang
              {folder === "inbox" && unreadCount > 0 ? (
                <span className="rounded-full bg-status-orange/20 px-1.5 text-[0.65rem] font-semibold text-status-orange">
                  {unreadCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={folder === "sent"}
              className={clsx("eh-segmented-item flex items-center gap-1.5", folder === "sent" && "is-active")}
              onClick={() => setFolder("sent")}
            >
              <Send size={14} aria-hidden />
              Postausgang
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="relative block min-w-[14rem] flex-1">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              type="search"
              className="eh-input w-full pl-9"
              placeholder={
                folder === "inbox" ? "Betreff oder Absender…" : "Betreff oder Empfänger…"
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </label>
          <Button type="button" variant="secondary" onClick={() => navigate("/mail/rundmail?bereich=vorlagen")}>
            <Send size={16} className="mr-1.5 inline" aria-hidden />
            E-Mails
          </Button>
          <Button type="button" variant="secondary" onClick={() => void loadMessages()}>
            <RefreshCw size={16} className="mr-1.5 inline" aria-hidden />
            Aktualisieren
          </Button>
        </div>
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <div className="grid min-h-[32rem] items-start gap-4 lg:grid-cols-[minmax(16rem,19rem)_minmax(0,1fr)]">
        <section className="overflow-hidden rounded-xl border border-separator bg-card">
          <div className="border-b border-separator px-4 py-3">
            <h2 className="text-sm font-semibold text-label">{folderLabel}</h2>
            {!loading ? (
              <p className="text-xs text-label-secondary">
                {messages.length} {messages.length === 1 ? "Nachricht" : "Nachrichten"}
                {folder === "inbox" && unreadCount > 0
                  ? ` · ${unreadCount} ungelesen`
                  : null}
              </p>
            ) : null}
          </div>
          {loading ? (
            <p className="px-4 py-8 text-sm text-label-secondary">Lade Nachrichten…</p>
          ) : messages.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-label-secondary">{emptyListText}</p>
          ) : (
            <ul ref={listRef} className="max-h-[calc(100dvh-16rem)] overflow-y-auto">
              {messages.map((message) => {
                const active = message.id === selectedId;
                const guestMatch = message.guest_match;
                const banned = Boolean(guestMatch?.is_place_banned);
                return (
                  <li key={message.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={clsx(
                        "w-full cursor-pointer border-b border-separator/70 px-4 py-3 text-left transition",
                        guestMatch && !banned && "border-l-[3px] border-l-accent",
                        banned && "border-l-[3px] border-l-status-red",
                        active
                          ? "bg-accent-soft"
                          : banned
                            ? "bg-status-red/10 hover:bg-status-red/15"
                            : guestMatch
                              ? "bg-accent-soft/80 hover:bg-accent-soft"
                              : !message.is_read
                                ? "bg-fill-tertiary/40 hover:bg-fill-tertiary/60"
                                : "hover:bg-fill-tertiary/60",
                      )}
                      onClick={() => setSelectedId(message.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedId(message.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className={clsx(
                            "truncate text-sm",
                            message.is_read ? "font-medium text-label" : "font-bold text-label",
                          )}
                        >
                          {messageContactLabel(message)}
                        </span>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="text-[0.65rem] text-label-tertiary">
                            {formatMailTimestamp(messageTimestamp(message))}
                          </span>
                          <ReadStatusBadge isRead={message.is_read} />
                        </div>
                      </div>
                      <p
                        className={clsx(
                          "mt-0.5 truncate text-sm",
                          message.is_read ? "text-label-secondary" : "font-medium text-label",
                        )}
                      >
                        {message.subject}
                      </p>
                      <p className="mt-1 line-clamp-2 text-xs text-label-tertiary">
                        {message.body_preview}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {guestMatch ? <GuestMatchBadge guest={guestMatch} /> : null}
                        {message.has_attachments ? (
                          <span className="inline-flex items-center gap-1 text-[0.65rem] text-label-tertiary">
                            <Paperclip size={12} aria-hidden />
                            Anhang
                          </span>
                        ) : null}
                        {readStateLive && message.is_read ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-[0.65rem] font-medium text-accent hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              setMessageReadState(message.id, false);
                            }}
                          >
                            <MailOpen size={12} aria-hidden />
                            Als ungelesen
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
              {nextSkip !== null ? (
                <li ref={sentinelRef} aria-hidden className="px-4 py-3 text-center text-xs text-label-tertiary">
                  Weitere Nachrichten werden geladen…
                </li>
              ) : null}
            </ul>
          )}
        </section>

        <section className="min-w-0 overflow-hidden rounded-xl border border-separator bg-card lg:min-h-[calc(100dvh-12rem)]">
          {!selectedSummary ? (
            <div className="flex h-full min-h-[20rem] items-center justify-center px-6 py-12 text-center text-sm text-label-secondary">
              Wählen Sie eine Nachricht aus, um den Inhalt und Gastinformationen anzuzeigen.
            </div>
          ) : detailLoading ? (
            <p className="px-6 py-12 text-sm text-label-secondary">Lade Nachricht…</p>
          ) : detail ? (
            <div className="flex h-full min-h-[32rem] flex-col lg:min-h-[calc(100dvh-12rem)]">
              <header className="border-b border-separator px-5 py-4 lg:px-8">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="min-w-0 flex-1 text-lg font-semibold text-label">{detail.subject}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <ReadStatusBadge isRead={detail.is_read} />
                    {folder === "inbox" && canReply ? (
                      <Button
                        type="button"
                        size="sm"
                        leftIcon={<Reply size={14} aria-hidden />}
                        loading={replyPreparing}
                        onClick={() => void openReservationReply()}
                      >
                        {inquiry?.arrivalDate ? "Anfrage übernehmen" : "Reservierung beantworten"}
                      </Button>
                    ) : null}
                    {readStateLive ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        leftIcon={<MailOpen size={14} aria-hidden />}
                        onClick={() => setMessageReadState(detail.id, !detail.is_read)}
                      >
                        {detail.is_read ? "Als ungelesen markieren" : "Als gelesen markieren"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {folder === "inbox" && "from_address" in detail ? (
                  <p className="mt-1 text-sm text-label-secondary">
                    Von{" "}
                    <span className="font-medium text-label">
                      {detail.from_name
                        ? `${detail.from_name} <${detail.from_address}>`
                        : detail.from_address}
                    </span>
                  </p>
                ) : "from_address" in detail ? (
                  <p className="mt-1 text-sm text-label-secondary">
                    Von{" "}
                    <span className="font-medium text-label">
                      {detail.from_name
                        ? `${detail.from_name} <${detail.from_address}>`
                        : detail.from_address}
                    </span>
                    {" · "}
                    An{" "}
                    <span className="font-medium text-label">
                      {detail.to_addresses.length > 0
                        ? detail.to_addresses.join(", ")
                        : "to_address" in detail
                          ? detail.to_address
                          : "—"}
                    </span>
                  </p>
                ) : null}
                <p className="text-xs text-label-tertiary">
                  {formatMailTimestamp(
                    "received_at" in detail ? detail.received_at : detail.sent_at,
                  )}
                  {folder === "inbox" && detail.to_addresses.length > 0
                    ? ` · An ${detail.to_addresses.join(", ")}`
                    : null}
                </p>
              </header>
              {detail.guest ? (
                <div
                  className={clsx(
                    "border-b border-separator px-5 py-3 lg:px-8",
                    detail.guest.is_place_banned ? "bg-status-red/10" : "bg-accent-soft/70",
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <GuestMatchBadge
                        guest={{
                          id: detail.guest.id,
                          first_name: detail.guest.first_name,
                          last_name: detail.guest.last_name,
                          is_place_banned: Boolean(detail.guest.is_place_banned),
                        }}
                      />
                      <span className="text-sm text-label-secondary">
                        {guestTypeLabel(detail.guest.guest_type)}
                      </span>
                      <Link
                        to={`/guests/${detail.guest.id}`}
                        className="text-sm font-medium text-accent hover:underline"
                      >
                        Stammkartei
                      </Link>
                      {canReply && !detail.guest.privacy_erased_at ? (
                        <Link
                          to={`/guests/${detail.guest.id}?edit=1`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
                        >
                          <Pencil size={14} aria-hidden />
                          Bearbeiten
                        </Link>
                      ) : null}
                      {canErase && inquiry?.erasureRequest && !detail.guest.privacy_erased_at ? (
                        <Link
                          to={`/guests/${detail.guest.id}?erase=1`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-status-red hover:underline"
                        >
                          <ShieldAlert size={14} aria-hidden />
                          Datenlöschung
                        </Link>
                      ) : null}
                      {openGuestStays.slice(0, 3).map((stay) => (
                        <Link
                          key={stay.id}
                          to={buildBookingsOpenUrl(stay.id, stay.status)}
                          className="text-sm font-medium text-accent hover:underline"
                        >
                          {stay.booking_number}
                          {inquiry?.arrivalDate &&
                          stay.arrival_date === inquiry.arrivalDate &&
                          stay.departure_date === inquiry.departureDate
                            ? " · gleicher Zeitraum"
                            : ` · ${formatIsoDateShort(stay.arrival_date)}`}
                        </Link>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm font-medium text-label-secondary hover:text-label"
                      aria-expanded={guestDetailsOpen}
                      onClick={() => setGuestDetailsOpen((open) => !open)}
                    >
                      <ChevronDown
                        size={16}
                        aria-hidden
                        className={clsx("transition", guestDetailsOpen && "rotate-180")}
                      />
                      {guestDetailsOpen ? "Details ausblenden" : "Gastdetails"}
                    </button>
                  </div>
                  {guestDetailsOpen ? (
                    <div className="mt-3">
                      <GuestMailContext guest={detail.guest} stays={detail.guest_stays} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="border-b border-separator px-5 py-2 text-xs text-label-secondary lg:px-8">
                  Kein Gast zugeordnet
                  {" · "}
                  <span className="font-mono">
                    {folder === "inbox" && "from_address" in detail
                      ? detail.from_address
                      : "to_address" in detail
                        ? detail.to_address
                        : "—"}
                  </span>
                </p>
              )}
              {folder === "inbox" && inquiry?.erasureRequest ? (
                <div className="border-b border-separator bg-status-orange/10 px-5 py-3 text-sm text-label lg:px-8">
                  Löschanfrage erkannt.
                  {detail.guest
                    ? canErase
                      ? " Über Datenlöschung geht es direkt in die Löschung dieses Gastes."
                      : " Ein Admin kann die Löschung in der Stammkartei ausführen."
                    : " Zur Absenderadresse ist kein Gast hinterlegt."}
                </div>
              ) : null}
              {folder === "inbox" && inquiry && !inquiry.erasureRequest && inquiry.summary ? (
                <div className="border-b border-separator bg-fill-tertiary/40 px-5 py-3 lg:px-8">
                  <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                    Aus der Mail erkannt
                  </p>
                  <p className="mt-1 text-sm text-label">{inquiry.summary}</p>
                  {overlappingStay ? (
                    <p className="mt-1 text-xs text-status-orange">
                      Der Zeitraum überschneidet sich mit {overlappingStay.booking_number}.
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-8">
                <MailMessageBody html={detail.body_html} text={detail.body_text} />
              </div>
            </div>
          ) : null}
        </section>
      </div>
      <PhoneBookingHoldModal
        open={replyOpen}
        onClose={() => setReplyOpen(false)}
        categories={categories}
        seed={replySeed}
        onCreated={() => setReplyOpen(true)}
      />
    </div>
  );
}
